import { useSignIn, useSignUp } from "@clerk/clerk-react";
import { isClerkAPIResponseError } from "@clerk/clerk-react/errors";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "#/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "#/components/ui/dialog";
import { Input } from "#/components/ui/input";
import { Sheet, SheetContent, SheetTitle } from "#/components/ui/sheet";
import { cn, useIsDesktop } from "#/lib/utils";

type Mode = "sign-in" | "sign-up";
type Step = "form" | "verify";

interface FormState {
	email: string;
	password: string;
	code: string;
	showPassword: boolean;
}

interface Errors {
	email?: string;
	password?: string;
	code?: string;
	global?: string;
}

function GoogleIcon({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			aria-hidden
			className={cn("shrink-0", className)}
			width="18"
			height="18"
		>
			<path
				fill="#4285F4"
				d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
			/>
			<path
				fill="#34A853"
				d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
			/>
			<path
				fill="#FBBC05"
				d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
			/>
			<path
				fill="#EA4335"
				d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
			/>
		</svg>
	);
}

function FieldLabel({ children }: { children: React.ReactNode }) {
	return (
		<span className="text-[10px] font-[800] uppercase tracking-[0.16em] text-foreground">
			{children}
		</span>
	);
}

function FieldError({ message }: { message?: string }) {
	if (!message) return null;
	return (
		<p role="alert" className="text-[0.75rem] text-destructive leading-snug">
			{message}
		</p>
	);
}

function OrDivider() {
	return (
		<div className="flex items-center gap-3" aria-hidden>
			<div className="h-px flex-1 bg-border" />
			<span className="text-[10px] font-[800] uppercase tracking-[0.18em] text-muted-foreground">
				or
			</span>
			<div className="h-px flex-1 bg-border" />
		</div>
	);
}

interface ContentProps {
	mode: Mode;
	setMode: (m: Mode) => void;
	onClose: () => void;
}

function SignInContent({ mode, setMode, onClose }: ContentProps) {
	const {
		isLoaded: signInLoaded,
		signIn,
		setActive: setSignInActive,
	} = useSignIn();
	const {
		isLoaded: signUpLoaded,
		signUp,
		setActive: setSignUpActive,
	} = useSignUp();

	const [step, setStep] = useState<Step>("form");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [form, setForm] = useState<FormState>({
		email: "",
		password: "",
		code: "",
		showPassword: false,
	});
	const [errors, setErrors] = useState<Errors>({});

	const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
		setForm((prev) => ({ ...prev, [key]: value }));

	const setError = (field: keyof Errors, msg: string) =>
		setErrors((prev) => ({ ...prev, [field]: msg }));

	const clearErrors = () => setErrors({});

	useEffect(() => {
		setStep("form");
		clearErrors();
		setForm((prev) => ({ ...prev, password: "", code: "" }));
	}, [mode]);

	function parseClerkError(
		err: unknown,
		fallback = "Something went wrong. Try again.",
	) {
		if (isClerkAPIResponseError(err)) {
			const mapped: Errors = {};
			for (const e of err.errors) {
				const param = e.meta?.paramName as string | undefined;
				if (param === "identifier" || e.code === "form_identifier_not_found") {
					mapped.email = e.message;
				} else if (param === "email_address" || e.code?.includes("email")) {
					mapped.email = e.message;
				} else if (param === "password" || e.code?.includes("password")) {
					mapped.password = e.message;
				} else if (e.code === "form_code_incorrect" || param === "code") {
					mapped.code = e.message;
				} else {
					mapped.global = e.message;
				}
			}
			return mapped;
		}
		return { global: fallback };
	}

	async function handleSignIn(e: React.FormEvent) {
		e.preventDefault();
		if (!signInLoaded || !signIn) return;
		clearErrors();
		setIsSubmitting(true);
		try {
			const result = await signIn.create({
				identifier: form.email,
				password: form.password,
			});
			if (result.status === "complete" && setSignInActive) {
				await setSignInActive({ session: result.createdSessionId });
				onClose();
			}
		} catch (err) {
			setErrors(parseClerkError(err));
		} finally {
			setIsSubmitting(false);
		}
	}

	async function handleSignUp(e: React.FormEvent) {
		e.preventDefault();
		if (!signUpLoaded || !signUp) return;
		clearErrors();
		setIsSubmitting(true);
		try {
			await signUp.create({
				emailAddress: form.email,
				password: form.password,
			});
			await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
			setStep("verify");
		} catch (err) {
			setErrors(parseClerkError(err));
		} finally {
			setIsSubmitting(false);
		}
	}

	async function handleVerify(e: React.FormEvent) {
		e.preventDefault();
		if (!signUpLoaded || !signUp) return;
		clearErrors();
		setIsSubmitting(true);
		try {
			const result = await signUp.attemptEmailAddressVerification({
				code: form.code,
			});
			if (result.status === "complete" && setSignUpActive) {
				await setSignUpActive({ session: result.createdSessionId });
				onClose();
			}
		} catch (err) {
			setErrors(parseClerkError(err));
		} finally {
			setIsSubmitting(false);
		}
	}

	async function handleGoogle() {
		if (!signInLoaded || !signIn) return;
		try {
			await signIn.authenticateWithRedirect({
				strategy: "oauth_google",
				redirectUrl: `${window.location.origin}/sso-callback`,
				redirectUrlComplete: window.location.origin,
			});
		} catch {
			setError("global", "Could not connect to Google. Please try again.");
		}
	}

	const title =
		mode === "sign-in"
			? "SIGN IN"
			: step === "verify"
				? "CHECK EMAIL"
				: "CREATE ACCOUNT";

	const subtitle =
		mode === "sign-in"
			? "Welcome back to the marketplace"
			: step === "verify"
				? `We sent a 6-digit code to ${form.email}`
				: "Join the disc golf marketplace";

	return (
		<div className="flex flex-col gap-5 p-6">
			{/* Header */}
			<div className="relative flex flex-col items-center gap-1 pb-1 text-center">
				{/* Decorative concentric disc rings */}
				<div
					aria-hidden
					className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-28 rounded-full border border-primary/10"
				/>
				<div
					aria-hidden
					className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-16 rounded-full border border-primary/15"
				/>
				<span className="relative font-display text-[11px] tracking-[0.2em] text-primary-accessible">
					RE:DISC
				</span>
				<h2 className="relative font-display text-[2rem] leading-[1.05] tracking-[0.04em] text-foreground">
					{title}
				</h2>
				<p className="relative text-[0.8125rem] leading-snug text-muted-foreground">
					{subtitle}
				</p>
			</div>

			{step === "verify" ? (
				/* ── Verification step ── */
				<form onSubmit={handleVerify} className="flex flex-col gap-4">
					{errors.global && (
						<p role="alert" className="text-[0.8125rem] text-destructive">
							{errors.global}
						</p>
					)}
					<div className="flex flex-col gap-1.5">
						<FieldLabel>Verification code</FieldLabel>
						<Input
							value={form.code}
							onChange={(e) => setField("code", e.target.value)}
							placeholder="000000"
							maxLength={6}
							inputMode="numeric"
							autoComplete="one-time-code"
							aria-invalid={!!errors.code}
							autoFocus
							className="text-center text-lg tracking-[0.3em] font-mono"
						/>
						<FieldError message={errors.code} />
					</div>
					<Button
						type="submit"
						className="w-full"
						disabled={isSubmitting || form.code.length < 6}
					>
						{isSubmitting ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							"Verify email"
						)}
					</Button>
					<button
						type="button"
						className="text-center text-[0.8125rem] text-muted-foreground transition-colors hover:text-foreground"
						onClick={() => setStep("form")}
					>
						← Back
					</button>
				</form>
			) : (
				<>
					{/* ── Google OAuth ── */}
					<Button
						type="button"
						variant="outline"
						className="w-full gap-2.5"
						onClick={handleGoogle}
					>
						<GoogleIcon />
						Continue with Google
					</Button>

					<OrDivider />

					{/* ── Email / password form ── */}
					<form
						onSubmit={mode === "sign-in" ? handleSignIn : handleSignUp}
						className="flex flex-col gap-4"
					>
						{errors.global && (
							<p role="alert" className="text-[0.8125rem] text-destructive">
								{errors.global}
							</p>
						)}

						{/* Email */}
						<div className="flex flex-col gap-1.5">
							<FieldLabel>Email</FieldLabel>
							<Input
								type="email"
								value={form.email}
								onChange={(e) => setField("email", e.target.value)}
								placeholder="you@example.com"
								autoComplete="email"
								aria-invalid={!!errors.email}
								autoFocus
								required
							/>
							<FieldError message={errors.email} />
						</div>

						{/* Password */}
						<div className="flex flex-col gap-1.5">
							<FieldLabel>Password</FieldLabel>
							<div className="relative">
								<Input
									type={form.showPassword ? "text" : "password"}
									value={form.password}
									onChange={(e) => setField("password", e.target.value)}
									placeholder={
										mode === "sign-up" ? "Create a password" : "••••••••"
									}
									autoComplete={
										mode === "sign-in" ? "current-password" : "new-password"
									}
									className="pr-10"
									aria-invalid={!!errors.password}
									required
								/>
								<button
									type="button"
									tabIndex={-1}
									aria-label={
										form.showPassword ? "Hide password" : "Show password"
									}
									className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
									onClick={() => setField("showPassword", !form.showPassword)}
								>
									{form.showPassword ? (
										<EyeOff className="h-4 w-4" />
									) : (
										<Eye className="h-4 w-4" />
									)}
								</button>
							</div>
							<FieldError message={errors.password} />
						</div>

						<Button
							type="submit"
							className="w-full"
							disabled={isSubmitting || !form.email || !form.password}
						>
							{isSubmitting ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : mode === "sign-in" ? (
								"Sign in"
							) : (
								"Create account"
							)}
						</Button>
					</form>
				</>
			)}

			{/* Footer toggle */}
			{step !== "verify" && (
				<p className="text-center text-[0.8125rem] text-muted-foreground">
					{mode === "sign-in" ? (
						<>
							Don&apos;t have an account?{" "}
							<button
								type="button"
								className="font-bold text-foreground transition-colors hover:text-primary"
								onClick={() => setMode("sign-up")}
							>
								Sign up
							</button>
						</>
					) : (
						<>
							Already have an account?{" "}
							<button
								type="button"
								className="font-bold text-foreground transition-colors hover:text-primary"
								onClick={() => setMode("sign-in")}
							>
								Sign in
							</button>
						</>
					)}
				</p>
			)}
		</div>
	);
}

export function SignInModal({
	isOpen,
	onClose,
}: {
	isOpen: boolean;
	onClose: () => void;
}) {
	const isDesktop = useIsDesktop();
	const [mode, setMode] = useState<Mode>("sign-in");

	useEffect(() => {
		if (!isOpen) setMode("sign-in");
	}, [isOpen]);

	const sharedContentClass =
		"bg-card p-0 border border-border border-t-2 border-t-primary shadow-[var(--shadow-raised)]";

	const content = (
		<SignInContent mode={mode} setMode={setMode} onClose={onClose} />
	);

	const srTitle = mode === "sign-in" ? "Sign in" : "Create account";

	if (isDesktop) {
		return (
			<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
				<DialogContent
					className={cn(sharedContentClass, "sm:max-w-sm overflow-hidden")}
					aria-describedby={undefined}
				>
					<DialogTitle className="sr-only">{srTitle}</DialogTitle>
					{content}
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<SheetContent
				side="bottom"
				showCloseButton={false}
				className={cn(
					sharedContentClass,
					"rounded-t-xl max-h-[90dvh] overflow-y-auto",
					"border-l-0 border-r-0 border-b-0",
				)}
			>
				<SheetTitle className="sr-only">{srTitle}</SheetTitle>
				<div className="flex justify-center pb-1 pt-3" aria-hidden>
					<div className="h-1.5 w-10 rounded-full bg-muted-foreground/25" />
				</div>
				{content}
			</SheetContent>
		</Sheet>
	);
}
